import { useMemo } from "react";
import { Link, useSearchParams } from "react-router";
import { useQuery } from "@tanstack/react-query";
import type { DeliveryReport } from "@aura/shared";
import { ed, field, tbl } from "~/components/editorial";
import { api } from "~/lib/api";
import { fmtInt, plural } from "~/lib/format";
import { CompareTable } from "./CompareTable";
import { botsQuery, exportHref, useBotsParam, useCompareParam } from "./lib";
import { BotsToggle, Byline, Controls, ReportShell, SectionHead, Segments } from "./parts";
import { deliveryHeadline } from "./story";

// By org, by product and tier, and by repo, for the last two complete periods.
export function BreakdownPage() {
  const { includeBots, setIncludeBots } = useBotsParam();
  const [compare, setCompare] = useCompareParam();
  const [params, setParams] = useSearchParams();
  const org = params.get("org") ?? "";
  const product = params.get("product") ?? "";
  const tier = params.get("tier") ?? "";

  const { data, isLoading } = useQuery({
    queryKey: ["delivery", compare, includeBots],
    queryFn: () => api.get<{ data: DeliveryReport }>(`/delivery?compare=${compare}${botsQuery(includeBots)}`),
    staleTime: 5 * 60 * 1000,
  });
  const r = data?.data;

  const setFilter = (key: string, value: string) =>
    setParams(
      (prev) => {
        const next = new URLSearchParams(prev);
        if (value) next.set(key, value);
        else next.delete(key);
        return next;
      },
      { replace: true },
    );

  const repoRows = useMemo(
    () =>
      (r?.byRepo ?? []).filter(
        (x) =>
          (!org || x.org === org) &&
          (!product || x.productKey === product) &&
          (!tier || (x.tier ?? "none") === tier),
      ),
    [r, org, product, tier],
  );
  const repoFilters = Object.fromEntries(Object.entries({ org, product, tier }).filter(([, v]) => v));

  return (
    <ReportShell meta={r ? `${r.compare.current.label} vs. ${r.compare.previous.label}` : undefined}>
      <Controls>
        <Segments
          label="Compare"
          value={compare}
          onChange={setCompare}
          options={[
            { value: "month", label: "Month" },
            { value: "quarter", label: "Quarter" },
          ]}
        />
        <BotsToggle includeBots={includeBots} onChange={setIncludeBots} />
      </Controls>
      {isLoading ? (
        <div className={ed.loading}>Loading the breakdown…</div>
      ) : !r ? (
        <div className={ed.loading}>Couldn't load the breakdown.</div>
      ) : (
        <>
          <div className={ed.kicker}>
            Engineering delivery · breakdown, {r.compare.current.label} vs. {r.compare.previous.label}
          </div>
          <h1 className={ed.headlineCompact}>{deliveryHeadline(r)}</h1>
          <Byline
            title="Breakdown"
            meta={r}
            extra={includeBots ? undefined : `${fmtInt(r.botPrsExcluded)} of them in these two periods.`}
          />

          <div className={ed.sect}>I. By org</div>
          <SectionHead title="Merged PRs by GitHub org" href={exportHref("org", includeBots, compare)} />
          <CompareTable
            nameHead="Org"
            periods={{ previous: r.compare.previous.short, current: r.compare.current.short }}
            rows={r.byOrg.rows}
            totals={[{ row: r.byOrg.total, label: "Total" }]}
          />

          <div className={ed.sect}>II. By product</div>
          <SectionHead title="Merged PRs by product and tier" href={exportHref("product", includeBots, compare)} />
          <CompareTable
            nameHead="Product"
            periods={{ previous: r.compare.previous.short, current: r.compare.current.short }}
            rows={r.byProduct.rows}
            extra={[{ head: "Tier", cell: (x) => x.tierLabel }]}
            totals={[{ row: r.byProduct.pxeTotal, label: "PxE total" }]}
          />
          <p className={ed.methodNarrow}>
            The PxE total leaves out Outside PxE. Products and tiers are set in{" "}
            <Link to="/settings/products">Admin, Products</Link>.
            {r.unmappedRepos.length > 0 &&
              ` ${plural(r.unmappedRepos.length, "repo")} with merged PRs ${r.unmappedRepos.length === 1 ? "has" : "have"} no product: ${r.unmappedRepos.map((x) => x.repo).join(", ")}.`}
          </p>

          <div className={ed.sect}>III. By repo</div>
          <SectionHead title="Merged PRs by repo" href={exportHref("repo", includeBots, compare, repoFilters)} />
          <div className={tbl.controls}>
            <select className={field.select} aria-label="Org" value={org} onChange={(e) => setFilter("org", e.target.value)}>
              <option value="">All orgs</option>
              {r.byOrg.rows.map((o) => (
                <option key={o.key} value={o.key}>
                  {o.label}
                </option>
              ))}
            </select>
            <select className={field.select} aria-label="Product" value={product} onChange={(e) => setFilter("product", e.target.value)}>
              <option value="">All products</option>
              {r.byProduct.rows.map((x) => (
                <option key={x.key} value={x.key}>
                  {x.label}
                </option>
              ))}
            </select>
            <select className={field.select} aria-label="Tier" value={tier} onChange={(e) => setFilter("tier", e.target.value)}>
              <option value="">All tiers</option>
              {[...new Map(r.byProduct.rows.map((x) => [x.tier ?? "none", x.tierLabel])).entries()].map(([key, label]) => (
                <option key={key} value={key}>
                  {label}
                </option>
              ))}
            </select>
            <span className={tbl.count}>
              {fmtInt(repoRows.length)} of {plural(r.byRepo.length, "repo")}
            </span>
          </div>
          <CompareTable
            nameHead="Repo"
            periods={{ previous: r.compare.previous.short, current: r.compare.current.short }}
            rows={repoRows}
            extra={[
              { head: "Product", cell: (x) => x.productName },
              { head: "Tier", cell: (x) => x.tierLabel },
            ]}
            empty="No repos match these filters."
          />
        </>
      )}
    </ReportShell>
  );
}
